/**
 * PIEPullToRefresh — visual indicator for the pull-to-refresh
 * gesture. Mounted at the top of `/mirror/dashboard`. Renders a
 * sticky pill that translates down with the user's pull and rotates
 * a chevron until the threshold is reached, at which point it flips
 * to a spinning loader and stays put while `refreshing` is true.
 *
 * Stateless — all motion is computed from incoming props so the page
 * (which owns the pull hook) controls timing.
 */
import React from 'react';
import { ArrowDown, Loader2 } from 'lucide-react';

export default function PIEPullToRefresh({ pullDistance = 0, refreshing = false, threshold = 70 }) {
  if (!refreshing && pullDistance <= 0) return null;
  // Active = past the trigger threshold; rotates the chevron 180° and
  // tints the pill so the user has clear feedback that release will
  // refresh.
  const active = refreshing || pullDistance >= threshold;
  // Translate the indicator with the pull so it feels physically
  // attached to the gesture. Cap at threshold even after overshoot —
  // overshoot adds a subtle resistance, not unbounded motion.
  const translateY = refreshing
    ? threshold * 0.55
    : Math.min(pullDistance, threshold * 1.1) - 28;
  // Chevron rotates from 0 → 180° as the user crosses the threshold.
  const rotateDeg = refreshing ? 0 : Math.min(180, (pullDistance / threshold) * 180);
  const opacity = refreshing ? 1 : Math.min(1, pullDistance / 28);

  return (
    <div
      className="pie-pull-indicator-wrap"
      data-testid="pie-pull-indicator"
      data-active={active ? '1' : '0'}
      data-refreshing={refreshing ? '1' : '0'}
      aria-hidden={!refreshing}
      role={refreshing ? 'status' : undefined}
      aria-live={refreshing ? 'polite' : undefined}
    >
      <div
        className={`pie-pull-indicator ${active ? 'pie-pull-indicator-active' : ''}`}
        style={{ transform: `translateY(${translateY}px)`, opacity }}
      >
        {refreshing
          ? (
            <>
              <Loader2 size={14} className="pie-pull-spinner" aria-hidden />
              <span>Refreshing…</span>
            </>
          )
          : (
            <>
              <ArrowDown
                size={14}
                aria-hidden
                style={{
                  transform: `rotate(${rotateDeg}deg)`,
                  transition: refreshing ? 'none' : 'transform 120ms ease',
                }}
              />
              <span>{active ? 'Release to refresh' : 'Pull down to refresh'}</span>
            </>
          )}
      </div>
    </div>
  );
}
